var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { convertToKAndM, DashboardStats, SearchInput, FilterTab, NotificationComp, Row, RowHeader, getAutoSlippage, } from 'ui-kit';
import { getIcon } from '../utils/getIcon';
import { getImage } from '../utils/getImage';
import { handleSortTransition, sortPools } from '../utils/sortUtils';
import { getDecimalPlaces, parseCurrencyString } from '../utils/currencyUtils';
import { formatHoverNumber } from '../utils/formatUtils';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEventSubscription, useSubscription } from '../hooks';
import { useStsuiSubscription } from '../hooks/useStsuiSubscription';
import { useAddressBalanceGuard } from '../hooks/useAddressBalanceGuard';
import { SwapModal } from '../components';
import { SwapButton } from '../components/SwapButton';
import { useCurrentWallet, useSignAndExecuteTransaction } from '../hooks/walletCompat';
import { useNetworkString } from '../context/NetworkContext';
import { useBalances } from '../context/BalanceContext';
import { transactionHandler } from '../utils/transactionHandlers';
import { handleClaimPendingWithdrawals, handleCancelPendingWithdrawals, SLUSH_WAL_LOOPING_POOL, } from '../utils/pendingWithdrawHandlers';
import { createAlphaFiSDK } from '../utils/networkUtils';
import { getButtonState } from '../utils/buttonStateUtils';
import { DEPOSIT_DISABLED_POOLS, isDepositDisabledPool } from '../utils/poolConstants';
import { alpha_pool_id, wbtc_layer0_coinType } from '../utils';
import { STSUI_COIN_TYPE, STSUI_DECIMALS } from '../utils/stsuiConstants';
import { SUI_MULTIPLIER_POOL_ID, SUI_MULTIPLIER_TOKEN_OPTIONS, getSuiMultiplierCoinType, getStsuiWalletBalance, convertSuiToStsui, isSuiMultiplierPool as checkIsSuiMultiplierPool, } from '../utils/suiMultiplierTokenChoice';
const AVAILABLE_TABS = ['All', 'ALPHA', 'SUI', 'stSUI', 'Stables', 'Slush', 'Holdings', 'Retired'];
const PoolsPage = ({ walletConnectFunctionality, themeName, isMobile, address, }) => {
    var _a, _b, _c, _d, _e, _f;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const getInitialTab = () => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            const matchedTab = AVAILABLE_TABS.find((tab) => tab.toLowerCase() === tabParam.toLowerCase());
            return matchedTab || 'All';
        }
        return 'All';
    };
    const [activeTab, setActiveTab] = useState(getInitialTab());
    const [searchTerm, setSearchTerm] = useState('');
    // Pools closed to new deposits start on the Withdraw tab instead of Deposit.
    const [poolTabStates, setPoolTabStates] = useState(() => DEPOSIT_DISABLED_POOLS.reduce((acc, disabledPoolId) => (Object.assign(Object.assign({}, acc), { [disabledPoolId]: 1 })), {}));
    const [poolExpansionStates, setPoolExpansionStates] = useState({});
    const [poolInputValues, setPoolInputValues] = useState({});
    const [poolMaxStates, setPoolMaxStates] = useState({});
    // Deposit/withdraw token choice for the SUI Multiplier pool (default 'SUI').
    const [depositTokenByPool, setDepositTokenByPool] = useState({});
    const [withdrawTokenByPool, setWithdrawTokenByPool] = useState({});
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
        spinner: false,
        link: '',
        image: undefined,
    });
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    // Which pool's transaction is in flight. `loading` alone can't place the button spinner:
    // every row reads the same boolean, so the row it appears on has to be identified
    // explicitly. Previously this was inferred from the quote state's poolId, which tracks the
    // last row the user *interacted* with (tab switch, MAX, typing) rather than the one that
    // started the transaction — so the spinner could land on a different row, or on none.
    const [loadingPoolId, setLoadingPoolId] = useState(null);
    const [loadingWithdrawalId, setLoadingWithdrawalId] = useState(null);
    const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
    const [isZapInActive, setIsZapInActive] = useState(true);
    const [strategyQuoteLoader, setStrategyQuoteLoader] = useState(false);
    const [zapInQuoteDetails, setZapInQuoteDetails] = useState({
        zapInLoader: true,
        zapInQuoteLoader: false,
        poolDetailsPage: false,
    });
    const [slippage, setSlippage] = useState(1);
    const [isAutoSlippage, setIsAutoSlippage] = useState(true);
    const tokenValueDebounceRefs = useRef({});
    const network = useNetworkString();
    const { walletBalancesMap } = useBalances();
    const checkAddressBalanceConflict = useAddressBalanceGuard();
    const { currentWallet } = useCurrentWallet();
    const signAndExecuteTransaction = useSignAndExecuteTransaction();
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            const matchedTab = AVAILABLE_TABS.find((tab) => tab.toLowerCase() === tabParam.toLowerCase());
            if (matchedTab) {
                setActiveTab(matchedTab);
            }
        }
        else {
            setActiveTab('All');
        }
    }, [searchParams]);
    useEffect(() => {
        const updateZapInputBoxIcons = () => {
            setZapInQuoteDetails((prev) => (Object.assign(Object.assign({}, prev), { reverseIcon: themeName === 'dark'
                    ? getIcon('reverseTokensIconDarkTheme')
                    : getIcon('reverseTokensIcon'), slippageIcon: themeName === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('zapSlippageIcon'), zapInIcon: themeName === 'dark' && isZapInActive
                    ? getIcon('zapInIcon')
                    : themeName === 'dark' && !isZapInActive
                        ? getIcon('zapInIconDarkTheme')
                        : themeName !== 'dark' && isZapInActive
                            ? getIcon('zapInIconDarkTheme')
                            : themeName !== 'dark' && !isZapInActive
                                ? getIcon('zapInIcon')
                                : '', depositsIcon: themeName === 'dark' ? getIcon('depositsIconForDarkTheme') : getIcon('depositsIcon'), poolDetailsPage: false })));
        };
        if (isZapInActive) {
            updateZapInputBoxIcons();
        }
    }, [themeName, isZapInActive]);
    const { data: staticData } = useEventSubscription('system', 'static_data');
    const { data: protocolData } = useSubscription('protocol');
    // useRef to keep the same address reference and prevent room changes
    const portfolioAddressRef = useRef(null);
    portfolioAddressRef.current = address || null;
    const { data: Portfolio } = useSubscription(portfolioAddressRef.current ? `portfolio:${portfolioAddressRef.current}` : null);
    const { data: coinInfoData } = useSubscription('coin_info');
    // stSUI/SUI exchange rate used to show the SUI Multiplier pool's SUI vault balance in stSUI on withdraw.
    const { data: stsuiStakingData } = useStsuiSubscription('room-staking-page');
    const stsuiExchangeRate = parseFloat((stsuiStakingData === null || stsuiStakingData === void 0 ? void 0 : stsuiStakingData.stSuiExchangeRate) || '0');
    const [sortConfig, setSortConfig] = useState(null);
    const [strategyQuoteDetails, setStrategyQuoteDetails] = useState(null);
    useEffect(() => {
        const debounceRefs = tokenValueDebounceRefs.current;
        return () => {
            Object.values(debounceRefs).forEach((timeout) => {
                if (timeout)
                    clearTimeout(timeout);
            });
        };
    }, []);
    const calculateTokenRatios = useCallback((poolId, inputTokenSymbol, inputValue, poolTokens) => {
        var _a, _b, _c;
        const amount = parseFloat(inputValue);
        if (!inputValue.trim() || isNaN(amount) || amount === 0) {
            return Object.fromEntries(poolTokens.map((t) => [t.symbol, '']));
        }
        if (poolTokens.length === 1) {
            return { [inputTokenSymbol]: inputValue };
        }
        setStrategyQuoteLoader(true);
        const immediateResult = Object.fromEntries(poolTokens.map((t) => [t.symbol, t.symbol === inputTokenSymbol ? inputValue : '']));
        try {
            const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
            const inputAssetType = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((a) => a.asset_symbol === inputTokenSymbol)) === null || _b === void 0 ? void 0 : _b.asset_type;
            const inputDecimals = Number(((_c = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[inputAssetType || '']) === null || _c === void 0 ? void 0 : _c.decimals) || 0);
            const amountInSmallestUnit = (amount * Math.pow(10, inputDecimals)).toString();
            const alphafiSdk = createAlphaFiSDK(network);
            const poolData = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
            const isAmountA = (poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
                ? !(poolTokens[0].symbol === inputTokenSymbol)
                : poolTokens[0].symbol === inputTokenSymbol;
            alphafiSdk
                .estimateLpAmounts({ poolId, amount: amountInSmallestUnit, isAmountA })
                .then(([amount1, amount2]) => {
                var _a, _b, _c;
                const tokenASymbol = poolTokens[0].symbol;
                const otherToken = poolTokens.find((t) => t.symbol !== inputTokenSymbol);
                if (!otherToken)
                    return;
                const otherAssetType = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((a) => a.asset_symbol === otherToken.symbol)) === null || _b === void 0 ? void 0 : _b.asset_type;
                const otherDecimals = Number(((_c = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[otherAssetType || '']) === null || _c === void 0 ? void 0 : _c.decimals) || 0);
                const smallestUnit = otherToken.symbol === tokenASymbol && !(poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
                    ? amount1
                    : otherToken.symbol !== tokenASymbol && !(poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
                        ? amount2
                        : otherToken.symbol === tokenASymbol && (poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
                            ? amount2
                            : amount1;
                const otherValueRaw = parseFloat(smallestUnit) / Math.pow(10, otherDecimals);
                const otherValue = otherValueRaw < 1
                    ? parseFloat(otherValueRaw.toFixed(8)).toString()
                    : parseFloat(otherValueRaw.toFixed(6)).toString();
                setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.assign(Object.assign({}, prev[poolId]), { [inputTokenSymbol]: inputValue, [otherToken.symbol]: otherValue }) })));
                setStrategyQuoteLoader(false);
            })
                .catch((err) => console.error('🔍 SDK Error calculating token ratios:', err));
        }
        catch (err) {
            console.error('⚠️ Error in calculateTokenRatios:', err);
            setStrategyQuoteLoader(false);
        }
        setStrategyQuoteLoader(false);
        return immediateResult;
    }, [staticData, coinInfoData, network]);
    useEffect(() => {
        // const poolData = pageData.pools.find((p) => p.pool.id === zapInQuoteDetails.poolId);
        if ((strategyQuoteDetails === null || strategyQuoteDetails === void 0 ? void 0 : strategyQuoteDetails.inputTokenSymbol) &&
            (strategyQuoteDetails === null || strategyQuoteDetails === void 0 ? void 0 : strategyQuoteDetails.poolTokens) &&
            (strategyQuoteDetails === null || strategyQuoteDetails === void 0 ? void 0 : strategyQuoteDetails.inputValue) &&
            (strategyQuoteDetails === null || strategyQuoteDetails === void 0 ? void 0 : strategyQuoteDetails.poolId) &&
            (strategyQuoteDetails === null || strategyQuoteDetails === void 0 ? void 0 : strategyQuoteDetails.poolId) !== '' &&
            (strategyQuoteDetails === null || strategyQuoteDetails === void 0 ? void 0 : strategyQuoteDetails.inputValue) !== '') {
            const interval = setInterval(() => {
                calculateTokenRatios(strategyQuoteDetails.poolId, strategyQuoteDetails.inputTokenSymbol, strategyQuoteDetails.inputValue, strategyQuoteDetails.poolTokens);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [strategyQuoteDetails, calculateTokenRatios]);
    const handleSwapNotification = (info) => {
        setNotificationInfo({
            title: info.title,
            description: info.description,
            spinner: info.spinner || false,
            link: info.link || '',
            image: info.image || undefined,
        });
        setIsNotificationOpen(true);
    };
    const suiBalance = (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get('0x2::sui::SUI')) || '0';
    // SUI Multiplier pool: deposit/withdraw SUI or stSUI
    const depositTokenOptions = SUI_MULTIPLIER_TOKEN_OPTIONS;
    const getSelectedDepositToken = (poolId) => depositTokenByPool[poolId] || 'SUI';
    const getDepositCoinType = (poolId) => getSuiMultiplierCoinType(getSelectedDepositToken(poolId));
    const isDepositTabActive = (poolId) => poolTabStates[poolId] === 0 || !(poolId in poolTabStates);
    // Input token: deposit swaps in stSUI if the user has selected stSUI for SUI Multiplier pool
    const buildRowTokens = (poolData) => {
        var _a, _b, _c, _d;
        const pid = poolData.pool.id;
        if (pid === SUI_MULTIPLIER_POOL_ID && isDepositTabActive(pid)) {
            if (getSelectedDepositToken(pid) === 'stSUI') {
                return [
                    {
                        symbol: 'stSUI',
                        name: 'stSUI',
                        icon: getIcon('stsui'),
                        walletBalance: getStsuiWalletBalance(walletBalancesMap),
                        price: ((_a = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[STSUI_COIN_TYPE]) === null || _a === void 0 ? void 0 : _a.price) || '',
                        value: ((_b = poolInputValues[pid]) === null || _b === void 0 ? void 0 : _b['stSUI']) || '',
                    },
                ];
            }
        }
        else if (pid === SUI_MULTIPLIER_POOL_ID &&
            getSelectedWithdrawToken(pid) === 'stSUI' &&
            poolData.tokens[0]) {
            const stsuiPrice = (_c = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[STSUI_COIN_TYPE]) === null || _c === void 0 ? void 0 : _c.price;
            return [
                Object.assign(Object.assign({}, poolData.tokens[0]), { symbol: 'stSUI', name: 'stSUI', icon: getIcon('stsui'), 
                    // Vault balance shown in stSUI
                    walletBalance: convertSuiToStsui(poolData.tokens[0].walletBalance, stsuiExchangeRate), price: stsuiPrice || poolData.tokens[0].price, value: ((_d = poolInputValues[pid]) === null || _d === void 0 ? void 0 : _d['stSUI']) || '' }),
            ];
        }
        return poolData.tokens;
    };
    // Switching the deposit token clears the pool's typed amount.
    const handleDepositTokenSelect = (poolId, symbol) => {
        if (symbol === getSelectedDepositToken(poolId))
            return;
        setDepositTokenByPool((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: symbol })));
        setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: {} })));
        setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: false })));
    };
    // Token to receive on withdraw.
    const getSelectedWithdrawToken = (poolId) => withdrawTokenByPool[poolId] || 'SUI';
    const getWithdrawCoinType = (poolId) => getSuiMultiplierCoinType(getSelectedWithdrawToken(poolId));
    const handleWithdrawTokenSelect = (poolId, symbol) => {
        if (symbol === getSelectedWithdrawToken(poolId))
            return;
        setWithdrawTokenByPool((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: symbol })));
        setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: {} })));
        setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: false })));
    };
    /**
     * `setIsTransactionLoading` for a pool row: tracks the in-flight pool alongside the
     * boolean so the button spinner renders on the row that started the transaction.
     */
    const setPoolTransactionLoading = (poolId) => (isTransactionLoading) => {
        setLoading(isTransactionLoading);
        setLoadingPoolId(isTransactionLoading ? poolId : null);
    };
    const handleDeposit = (poolId, amounts) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        if (!address) {
            console.error('No wallet connected');
            return;
        }
        if (isDepositDisabledPool(poolId)) {
            console.error('Deposits are disabled for this pool');
            return;
        }
        const pool = pageData.pools.find((p) => p.pool.id === poolId);
        const formattedAmounts = Object.entries(amounts)
            .map(([coin, amount]) => `${Number(amount).toFixed(getDecimalPlaces(coin))} ${coin}`)
            .join(' and ');
        const tokenSymbols = Object.keys(amounts);
        const poolData = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        if (checkAddressBalanceConflict(tokenSymbols.map((sym) => {
            var _a;
            // Guard against the chosen deposit coin type.
            if (poolId === SUI_MULTIPLIER_POOL_ID) {
                return { coinType: getDepositCoinType(poolId) };
            }
            const assetType = (_a = poolData === null || poolData === void 0 ? void 0 : poolData.asset_types) === null || _a === void 0 ? void 0 : _a.find((a) => a.asset_symbol === sym);
            return { coinType: assetType === null || assetType === void 0 ? void 0 : assetType.asset_type };
        }))) {
            setNotificationInfo({
                title: 'Wallet Update Required',
                description: `${(_a = currentWallet === null || currentWallet === void 0 ? void 0 : currentWallet.name) !== null && _a !== void 0 ? _a : 'Your wallet'} doesn't yet support address balances, a recent Sui network update. Your funds are safe — to continue, connect a supported wallet.`,
                image: getIcon('failed'),
                spinner: false,
            });
            setIsNotificationOpen(true);
            return;
        }
        const isAmountA = tokenSymbols.length > 1 && !(poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
            ? tokenSymbols.indexOf(Object.keys(amounts)[0]) === 0
            : tokenSymbols.length > 1 && (poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
                ? tokenSymbols.indexOf(Object.keys(amounts)[0]) === 1
                : undefined;
        const amount = Object.values(amounts)[0];
        const firstTokenSymbol = tokenSymbols[0];
        const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        const firstAssetType = (_c = (_b = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _b === void 0 ? void 0 : _b.find((asset) => asset.asset_symbol === firstTokenSymbol)) === null || _c === void 0 ? void 0 : _c.asset_type;
        // Chosen coin type + decimals (stSUI isn't in the pool's asset_types).
        const isSuiMultiplierPool = checkIsSuiMultiplierPool(poolId);
        const depositCoinType = isSuiMultiplierPool ? getDepositCoinType(poolId) : undefined;
        const depositDecimals = isSuiMultiplierPool && depositCoinType
            ? ((_d = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[depositCoinType]) === null || _d === void 0 ? void 0 : _d.decimals) || String(STSUI_DECIMALS)
            : (firstAssetType && ((_e = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[firstAssetType]) === null || _e === void 0 ? void 0 : _e.decimals)) || '';
        try {
            yield transactionHandler({
                poolName: (_f = pageData.pools.find((p) => p.pool.id === poolId)) === null || _f === void 0 ? void 0 : _f.pool.name,
                parentProtocols: pool === null || pool === void 0 ? void 0 : pool.pool.protocol,
                amount: amount,
                poolId: poolId,
                decimals: depositDecimals,
                // Forward the chosen deposit coin type to the SDK.
                coinType: depositCoinType,
                network: network,
                action: 'deposit',
                modalState: {
                    isOpen: true,
                    token: (pool === null || pool === void 0 ? void 0 : pool.tokens[0]) || null,
                    actionType: 'deposit',
                },
                address: address,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setPoolTransactionLoading(poolId),
                displayAmount: formattedAmounts,
                suiBalance: suiBalance,
                isAmountA: isAmountA,
                resetInputValues: () => {
                    setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.fromEntries((pool === null || pool === void 0 ? void 0 : pool.tokens.map((t) => [t.symbol, ''])) || []) })));
                    setStrategyQuoteDetails((prev) => (Object.assign(Object.assign({}, (prev || {})), { poolId: '', inputTokenSymbol: '', inputValue: '', poolTokens: [] })));
                    setZapInQuoteDetails((prev) => (Object.assign(Object.assign({}, prev), { zapInLoader: false, zapInQuoteLoader: false, poolId: '', activeToken: '', depositAmountWithoutExponent: '' })));
                    setIsAutoSlippage(true);
                },
            });
        }
        catch (error) {
            console.error('Deposit failed:', error);
        }
    });
    const handleZapInAndDeposit = (poolId) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        if (!address) {
            console.error('No wallet connected');
            return;
        }
        if (isDepositDisabledPool(poolId)) {
            console.error('Deposits are disabled for this pool');
            return;
        }
        const pool = pageData.pools.find((p) => p.pool.id === poolId);
        const formattedAmounts = `${zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Amount} ${zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol} and ${zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Amount} ${zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol}`;
        // const firstTokenSymbol = tokenSymbols[0];
        const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        const activeAsset = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((asset) => asset.asset_symbol === (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.activeToken))) === null || _b === void 0 ? void 0 : _b.asset_type;
        const poolData = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        try {
            yield transactionHandler({
                poolName: (_c = pageData.pools.find((p) => p.pool.id === poolId)) === null || _c === void 0 ? void 0 : _c.pool.name,
                parentProtocols: pool === null || pool === void 0 ? void 0 : pool.pool.protocol,
                amount: (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.depositAmountWithoutExponent) || '0',
                poolId: poolId,
                decimals: (activeAsset && ((_d = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[activeAsset]) === null || _d === void 0 ? void 0 : _d.decimals)) || '',
                network: network,
                action: 'zap_deposit',
                modalState: {
                    isOpen: true,
                    token: (pool === null || pool === void 0 ? void 0 : pool.tokens[0]) || null,
                    actionType: 'zap_deposit',
                },
                address: address,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setPoolTransactionLoading(poolId),
                displayAmount: formattedAmounts,
                suiBalance: suiBalance,
                isAmountA: (poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
                    ? !(zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA)
                    : zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA,
                slippage: slippage,
                resetInputValues: () => {
                    setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.fromEntries((pool === null || pool === void 0 ? void 0 : pool.tokens.map((t) => [t.symbol, ''])) || []) })));
                    setZapInQuoteDetails((prev) => {
                        var _a;
                        return (Object.assign(Object.assign({}, prev), { zapInLoader: true, zapInQuoteLoader: false, poolId: ((_a = pageData.pools.find((p) => p.pool.id === poolId)) === null || _a === void 0 ? void 0 : _a.pool.strategyType) ===
                                'Leveraged Yield Farming'
                                ? ''
                                : poolId, poolDetailsPage: false, zapSlippage: '', token1Amount: '', token2Amount: '' }));
                    });
                    setIsAutoSlippage(true);
                },
            });
        }
        catch (error) {
            console.error('Deposit failed:', error);
        }
    });
    const handleWithdraw = (poolId, amounts) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!address) {
            console.error('No wallet connected');
            return;
        }
        const pool = pageData.pools.find((p) => p.pool.id === poolId);
        const formattedAmounts = Object.entries(amounts)
            .map(([coin, amount]) => `${Number(amount).toFixed(getDecimalPlaces(coin))} ${coin}`)
            .join(' and ');
        const poolData = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        const tokenSymbols = Object.keys(amounts);
        const isAmountA = tokenSymbols.length > 1 && !(poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
            ? tokenSymbols.indexOf(Object.keys(amounts)[0]) === 0
            : tokenSymbols.length > 1 && (poolData === null || poolData === void 0 ? void 0 : poolData.is_reversed)
                ? tokenSymbols.indexOf(Object.keys(amounts)[0]) === 1
                : undefined;
        const amount = Object.values(amounts)[0];
        const firstTokenSymbol = tokenSymbols[0];
        const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        const firstAssetType = (_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a.find((asset) => asset.asset_symbol === firstTokenSymbol)) === null || _b === void 0 ? void 0 : _b.asset_type;
        // Withdraw amount decimals for SUI Multiplier pool
        const baseAssetType = (_d = (_c = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.asset_type;
        const withdrawDecimals = poolId === SUI_MULTIPLIER_POOL_ID && baseAssetType
            ? ((_e = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[baseAssetType]) === null || _e === void 0 ? void 0 : _e.decimals) || String(STSUI_DECIMALS)
            : (firstAssetType && ((_f = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[firstAssetType]) === null || _f === void 0 ? void 0 : _f.decimals)) || '';
        try {
            yield transactionHandler({
                poolName: (_g = pageData.pools.find((p) => p.pool.id === poolId)) === null || _g === void 0 ? void 0 : _g.pool.name,
                parentProtocols: pool === null || pool === void 0 ? void 0 : pool.pool.protocol,
                amount: amount,
                poolId: poolId,
                decimals: withdrawDecimals,
                // Forward the chosen token to receive.
                coinType: poolId === SUI_MULTIPLIER_POOL_ID ? getWithdrawCoinType(poolId) : undefined,
                network: network,
                action: 'withdraw',
                modalState: {
                    isOpen: true,
                    token: (pool === null || pool === void 0 ? void 0 : pool.tokens[0]) || null,
                    actionType: 'withdraw',
                },
                address: address,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setPoolTransactionLoading(poolId),
                displayAmount: formattedAmounts,
                suiBalance: suiBalance,
                isAmountA: isAmountA,
                withdrawMax: poolMaxStates[poolId] || false,
                resetInputValues: () => {
                    setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.fromEntries((pool === null || pool === void 0 ? void 0 : pool.tokens.map((t) => [t.symbol, ''])) || []) })));
                    setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: false })));
                    setStrategyQuoteDetails((prev) => (Object.assign(Object.assign({}, (prev || {})), { poolId: '', inputTokenSymbol: '', inputValue: '', poolTokens: [] })));
                    setIsAutoSlippage(true);
                },
            });
        }
        catch (error) {
            console.error('Withdraw failed:', error);
        }
    });
    // Helper function to format withdrawal amount with asset symbol
    const formatWithdrawalAmount = (poolId, withdrawalAmount) => {
        var _a, _b;
        const poolStatic = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === poolId);
        const firstAssetSymbol = ((_b = (_a = poolStatic === null || poolStatic === void 0 ? void 0 : poolStatic.asset_types) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.asset_symbol) || '';
        return `${Number(withdrawalAmount).toFixed(getDecimalPlaces(firstAssetSymbol))} ${firstAssetSymbol}`;
    };
    // Handler for claiming pending withdrawals for Slush pools with waiting period
    const handleClaimPendingWithdrawalsWrapper = (poolId, withdrawRequestId, withdrawalAmount) => __awaiter(void 0, void 0, void 0, function* () {
        const formattedAmount = formatWithdrawalAmount(poolId, withdrawalAmount);
        yield handleClaimPendingWithdrawals({
            poolId,
            withdrawRequestId,
            address: address || '',
            network,
            signAndExecuteTransaction,
            setNotificationInfo,
            setIsNotificationOpen,
            setLoadingWithdrawalId,
            getIcon: getIcon,
            displayAmount: formattedAmount,
        });
    });
    // Handler for canceling pending withdrawals for Slush pools
    const handleCancelPendingWithdrawalsWrapper = (poolId, withdrawRequestId, withdrawalAmount) => __awaiter(void 0, void 0, void 0, function* () {
        const formattedAmount = formatWithdrawalAmount(poolId, withdrawalAmount);
        yield handleCancelPendingWithdrawals({
            poolId,
            withdrawRequestId,
            address: address || '',
            network,
            signAndExecuteTransaction,
            setNotificationInfo,
            setIsNotificationOpen,
            setLoadingWithdrawalId,
            getIcon: getIcon,
            displayAmount: formattedAmount,
        });
    });
    const handleTokenInputChange = (poolId, tokenSymbol, value) => {
        const poolData = pageData.pools.find((p) => p.pool.id === poolId);
        if ((poolData === null || poolData === void 0 ? void 0 : poolData.tokens.length) === 2) {
            return;
        }
        setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: value.trim() === ''
                ? Object.fromEntries((poolData === null || poolData === void 0 ? void 0 : poolData.tokens.map((t) => [t.symbol, ''])) || [])
                : Object.assign(Object.assign({}, prev[poolId]), { [tokenSymbol]: value }) })));
    };
    const handleMaxClick = (poolId, tokenSymbol) => {
        var _a;
        setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: true })));
        const poolData = pageData.pools.find((p) => p.pool.id === poolId);
        // stSUI isn't in poolData.tokens, so fall back to the displayed tokens.
        const token = (poolData === null || poolData === void 0 ? void 0 : poolData.tokens.find((t) => t.symbol === tokenSymbol)) ||
            (poolData ? buildRowTokens(poolData).find((t) => t.symbol === tokenSymbol) : undefined);
        if (!(token === null || token === void 0 ? void 0 : token.walletBalance) || parseFloat(token.walletBalance) === 0) {
            return;
        }
        if (poolData && poolData.tokens.length === 2) {
            const immediateValues = {
                [tokenSymbol]: token.walletBalance,
                [((_a = poolData.tokens.find((t) => t.symbol !== tokenSymbol)) === null || _a === void 0 ? void 0 : _a.symbol) || '']: '',
            };
            setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: immediateValues })));
            if (poolData.tokens.length === 2 &&
                poolTabStates &&
                (poolTabStates[poolData.pool.id] === 0 || poolTabStates[poolData.pool.id] === undefined) &&
                isZapInActive) {
                fetchZapInQuote(token.walletBalance, tokenSymbol === poolData.tokens[0].symbol, poolData.pool.id, poolData.tokens);
            }
            else {
                setStrategyQuoteDetails((prev) => (Object.assign(Object.assign({}, (prev || {})), { poolId, inputTokenSymbol: tokenSymbol, inputValue: token.walletBalance, poolTokens: poolData.tokens })));
                calculateTokenRatios(poolId, tokenSymbol, token.walletBalance, poolData.tokens);
            }
        }
        else {
            setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: Object.assign(Object.assign({}, prev[poolId]), { [tokenSymbol]: token.walletBalance }) })));
        }
    };
    const fetchParentProtocolIconArray = (parent_protocol_name) => {
        if (typeof parent_protocol_name === 'string') {
            return [getIcon(parent_protocol_name)];
        }
        const protocolIcons = parent_protocol_name.map((protocol) => getIcon(protocol.toLowerCase()));
        return protocolIcons;
    };
    const pageData = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        if (!Array.isArray(staticData) || staticData.length === 0) {
            return {
                portfolio: {
                    balance: '0',
                    apy: '0',
                    apr: '0',
                    alphafiTvl: '0',
                    airdropReserves: '0',
                    rewardAmount: '0',
                },
                pools: [],
            };
        }
        let alphaVaultBalance = '0';
        let alphaVaultTVL = '0';
        const alphaVaultAprRaw = (_b = (_a = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data) === null || _a === void 0 ? void 0 : _a[alpha_pool_id]) === null || _b === void 0 ? void 0 : _b.apr;
        const alphaVaultAPY = Number.isFinite(Number(alphaVaultAprRaw)) ? alphaVaultAprRaw : '0';
        if ((Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.alpha_vault_portfolio.staked_alpha_usd) &&
            parseFloat(Portfolio.alpha_vault_portfolio.staked_alpha_usd) > 0) {
            alphaVaultBalance = Portfolio.alpha_vault_portfolio.staked_alpha_usd;
        }
        if (protocolData &&
            ((_c = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[alpha_pool_id]) === null || _c === void 0 ? void 0 : _c.tvl_usd) &&
            parseFloat((_d = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[alpha_pool_id]) === null || _d === void 0 ? void 0 : _d.tvl_usd) > 0) {
            alphaVaultTVL = (_e = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[alpha_pool_id]) === null || _e === void 0 ? void 0 : _e.tvl_usd;
        }
        const pools = staticData
            .filter((poolDetails) => poolDetails.pool_id)
            .map((poolDetails) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
            const { pool_id, pool_name, parent_protocol_name, asset_types, pool_tags } = poolDetails;
            const poolProtocolData = (_a = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data) === null || _a === void 0 ? void 0 : _a[pool_id];
            const apyHoverArray = poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.apy_hover;
            const pool = {
                id: pool_id,
                name: pool_name,
                protocol: parent_protocol_name,
                protocolIcon: fetchParentProtocolIconArray(parent_protocol_name),
                tvl: pool_name === 'ALPHA' ? alphaVaultTVL : (poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.tvl) || '0',
                strategyType: (_b = poolDetails.strategy_type_headers) === null || _b === void 0 ? void 0 : _b[0],
                apy: pool_name === 'ALPHA'
                    ? Number(alphaVaultAPY).toFixed(2)
                    : parseFloat((poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.apy) || '0') > 10000
                        ? '>10K'
                        : convertToKAndM((poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.apy) || '0'),
                yourDeposit: pool_name === 'ALPHA'
                    ? alphaVaultBalance
                    : ((_d = (_c = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.pool_balances) === null || _c === void 0 ? void 0 : _c[pool_id]) === null || _d === void 0 ? void 0 : _d.balance_usd) || '0',
                currentPrice: (() => {
                    var _a;
                    const poolData = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[pool_id];
                    if (poolData &&
                        'position_range' in poolData &&
                        poolData.position_range &&
                        ((_a = poolDetails.strategy_type_headers) === null || _a === void 0 ? void 0 : _a[0]) !== 'Leveraged Yield Farming') {
                        const { current_price } = poolData.position_range;
                        const price = parseFloat(current_price);
                        const assetSymbols = asset_types === null || asset_types === void 0 ? void 0 : asset_types.map((asset) => asset.asset_symbol);
                        if (assetSymbols && assetSymbols.length >= 2) {
                            return `${price.toFixed(5)} ${poolDetails.is_reversed ? assetSymbols[0] : assetSymbols[1]} per ${poolDetails.is_reversed ? assetSymbols[1] : assetSymbols[0]}`;
                        }
                        return `${price.toFixed(5)}`;
                    }
                    return undefined;
                })(),
                positionRange: (() => {
                    const poolData = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[pool_id];
                    if (poolData && 'position_range' in poolData && poolData.position_range) {
                        const { lower, upper } = poolData.position_range;
                        return `${parseFloat(lower).toFixed(5)} - ${parseFloat(upper).toFixed(5)}`;
                    }
                    return undefined;
                })(),
                rangeStatus: (() => {
                    const poolData = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data[pool_id];
                    if (poolData && 'position_range' in poolData && poolData.position_range) {
                        const { lower, upper, current_price } = poolData.position_range;
                        return Number(lower) <= Number(current_price) &&
                            Number(current_price) <= Number(upper)
                            ? 'inRange'
                            : 'outOfRange';
                    }
                    return undefined;
                })(),
                tab: (asset_types === null || asset_types === void 0 ? void 0 : asset_types.map((asset) => asset.asset_symbol)) || [],
                isActive: poolDetails.is_active,
                navTabs: [
                    { label: 'Deposit', disabled: isDepositDisabledPool(pool_id) },
                    { label: 'Withdraw', disabled: false },
                ],
            };
            if (pool_name === 'ALPHA') {
                pool.navTabs = [
                    { label: 'Deposit', disabled: isDepositDisabledPool(pool_id) },
                    { label: 'Withdraw', disabled: false, navigateTo: '/airdrop', isNavigation: true },
                    {
                        label: 'Locked',
                        disabled: false,
                        navigateTo: '/airdrop',
                        isNavigation: true,
                    },
                ];
            }
            return {
                pool,
                tokens: (asset_types === null || asset_types === void 0 ? void 0 : asset_types.map((asset) => {
                    var _a, _b;
                    return ({
                        symbol: asset.asset_symbol,
                        name: asset.asset_symbol,
                        icon: getIcon(asset.asset_type === wbtc_layer0_coinType
                            ? 'wbtc_layer0'
                            : asset.asset_symbol === 'wBTC'
                                ? 'suibtc'
                                : asset.asset_symbol === 'WBTC'
                                    ? 'btc'
                                    : asset.asset_symbol.toLowerCase()),
                        walletBalance: poolTabStates[pool_id] === 0 || !(pool_id in poolTabStates)
                            ? (parseFloat((walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(asset.asset_type)) || '0') /
                                Math.pow(10, asset.asset_decimals)).toString()
                            : (() => {
                                var _a;
                                const poolBalance = (_a = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.pool_balances) === null || _a === void 0 ? void 0 : _a[pool_id];
                                if (!poolBalance)
                                    return '0';
                                if ('balances' in poolBalance && poolBalance.balances) {
                                    const tokenBalance = poolBalance.balances.find((balance) => balance.label === asset.asset_type);
                                    if (!tokenBalance && asset.asset_symbol.toUpperCase() === 'ALPHA') {
                                        const lockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_locked');
                                        const unlockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_unlocked');
                                        if (lockedBalance || unlockedBalance) {
                                            const locked = parseFloat((lockedBalance === null || lockedBalance === void 0 ? void 0 : lockedBalance.value) || '0');
                                            const unlockedRaw = parseFloat((unlockedBalance === null || unlockedBalance === void 0 ? void 0 : unlockedBalance.value) || '0');
                                            const unlocked = unlockedRaw / Math.pow(10, asset.asset_decimals);
                                            return (locked + unlocked).toString();
                                        }
                                    }
                                    return (tokenBalance === null || tokenBalance === void 0 ? void 0 : tokenBalance.value) || '0';
                                }
                                return '0';
                            })(),
                        price: ((_a = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[asset.asset_type]) === null || _a === void 0 ? void 0 : _a.price) || '',
                        value: ((_b = poolInputValues[pool_id]) === null || _b === void 0 ? void 0 : _b[asset.asset_symbol]) || '',
                    });
                })) || [],
                tokensInPoolForTransaction: (asset_types === null || asset_types === void 0 ? void 0 : asset_types.map((asset) => {
                    var _a, _b;
                    return ({
                        symbol: asset.asset_symbol,
                        name: asset.asset_symbol,
                        icon: getIcon(asset.asset_symbol.toLowerCase(), asset.asset_symbol),
                        walletBalance: poolTabStates[pool_id] === 0 || !(pool_id in poolTabStates)
                            ? (parseFloat((walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(asset.asset_type)) || '0') /
                                Math.pow(10, asset.asset_decimals)).toString()
                            : (() => {
                                var _a;
                                const poolBalance = (_a = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.pool_balances) === null || _a === void 0 ? void 0 : _a[pool_id];
                                if (!poolBalance)
                                    return '0';
                                if ('balances' in poolBalance && poolBalance.balances) {
                                    const tokenBalance = poolBalance.balances.find((balance) => balance.label === asset.asset_type);
                                    if (!tokenBalance && asset.asset_symbol.toUpperCase() === 'ALPHA') {
                                        const lockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_locked');
                                        const unlockedBalance = poolBalance.balances.find((b) => b.label === 'alpha_unlocked');
                                        if (lockedBalance || unlockedBalance) {
                                            const locked = parseFloat((lockedBalance === null || lockedBalance === void 0 ? void 0 : lockedBalance.value) || '0');
                                            const unlockedRaw = parseFloat((unlockedBalance === null || unlockedBalance === void 0 ? void 0 : unlockedBalance.value) || '0');
                                            const unlocked = unlockedRaw / Math.pow(10, asset.asset_decimals);
                                            return (locked + unlocked).toString();
                                        }
                                    }
                                    return (tokenBalance === null || tokenBalance === void 0 ? void 0 : tokenBalance.value) || '0';
                                }
                                return '0';
                            })(),
                        price: ((_a = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[asset.asset_type]) === null || _a === void 0 ? void 0 : _a.price) || '',
                        value: ((_b = poolInputValues[pool_id]) === null || _b === void 0 ? void 0 : _b[asset.asset_symbol]) || '',
                    });
                })) || [],
                tags: (pool_tags === null || pool_tags === void 0 ? void 0 : pool_tags.map((tag) => ({
                    name: tag,
                    color: tag === 'LYF' && themeName === 'dark'
                        ? '#FFFFFF'
                        : tag === 'LYF' && themeName !== 'dark'
                            ? '#FFFFFF'
                            : '#cbe6ff',
                    style: tag === 'LYF' && themeName === 'dark'
                        ? {
                            color: '#FFFFFF',
                            border: '1px solid #FFFFFF',
                            fontFamily: 'InterSemiBold',
                            borderRadius: '30px',
                            paddingTop: '3px',
                            paddingBottom: '3px',
                        }
                        : tag === 'LYF' && themeName !== 'dark'
                            ? {
                                color: '#000',
                                border: '1px solid #293036',
                                fontFamily: 'InterSemiBold',
                                borderRadius: '30px',
                                paddingTop: '3px',
                                paddingBottom: '3px',
                            }
                            : undefined,
                }))) || [],
                tooltip: apyHoverArray
                    ? {
                        items: apyHoverArray.map((apyItem) => ({
                            label: apyItem.label,
                            value: `${parseFloat(apyItem.value).toFixed(2)}%`,
                        })),
                        infotext: `Compounding ${parent_protocol_name} Protocol APR Multiple Times a Day`,
                    }
                    : undefined,
                aboutStrategy: {
                    strategyParagraphs: {
                        paragraph1: ((_f = (_e = poolDetails.strategy_details) === null || _e === void 0 ? void 0 : _e.paragraphs) === null || _f === void 0 ? void 0 : _f[0]) || '',
                        paragraph2: ((_h = (_g = poolDetails.strategy_details) === null || _g === void 0 ? void 0 : _g.paragraphs) === null || _h === void 0 ? void 0 : _h[1]) || '',
                        paragraph3: ((_k = (_j = poolDetails.strategy_details) === null || _j === void 0 ? void 0 : _j.paragraphs) === null || _k === void 0 ? void 0 : _k[2]) || '',
                        paragraph4: ((_m = (_l = poolDetails.strategy_details) === null || _l === void 0 ? void 0 : _l.paragraphs) === null || _m === void 0 ? void 0 : _m[3]) || '',
                        paragraph5: ((_p = (_o = poolDetails.strategy_details) === null || _o === void 0 ? void 0 : _o.paragraphs) === null || _p === void 0 ? void 0 : _p[4]) || '',
                        paragraph6: ((_r = (_q = poolDetails.strategy_details) === null || _q === void 0 ? void 0 : _q.paragraphs) === null || _r === void 0 ? void 0 : _r[5]) || '',
                    },
                    performanceFee: `${((_s = poolProtocolData === null || poolProtocolData === void 0 ? void 0 : poolProtocolData.about_strategy_labels) === null || _s === void 0 ? void 0 : _s.performance_fee) || '0'}% of the Yield`,
                    rightArrow: getIcon('rightArrowWhite'),
                },
                // Scoped to this pool for the same reason as displayLoaderConditions — the bare
                // `loading` boolean is true for whichever row is transacting, not this one.
                isLoading: loading && loadingPoolId === pool_id,
                // Add pending withdrawals for pools with waiting period (like Slush USDC and Alpha Vault)
                pendingWithdrawals: pool_id === SLUSH_WAL_LOOPING_POOL
                    ? (() => {
                        var _a;
                        const poolBalance = (_a = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.pool_balances) === null || _a === void 0 ? void 0 : _a[pool_id];
                        if (poolBalance &&
                            'pending_withdrawals' in poolBalance &&
                            Array.isArray(poolBalance.pending_withdrawals)) {
                            return poolBalance.pending_withdrawals.map((withdrawal) => {
                                var _a, _b;
                                const withdrawalDate = withdrawal.withdrawal_eta
                                    ? new Date(parseFloat(withdrawal.withdrawal_eta))
                                    : null;
                                const date = withdrawalDate
                                    ? withdrawalDate
                                        .toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric',
                                    })
                                        .replace(/,/g, '')
                                    : null;
                                return {
                                    withdrawal_eta: date,
                                    withdrawal_amount: withdrawal.withdrawal_amount,
                                    withdrawal_status: withdrawal.withdrawal_status,
                                    icon: withdrawal.icon ||
                                        getIcon((_a = asset_types === null || asset_types === void 0 ? void 0 : asset_types[0]) === null || _a === void 0 ? void 0 : _a.asset_symbol.toLowerCase(), (_b = asset_types === null || asset_types === void 0 ? void 0 : asset_types[0]) === null || _b === void 0 ? void 0 : _b.asset_symbol),
                                    withdrawal_ticket_id: withdrawal.withdrawal_ticket_id,
                                };
                            });
                        }
                        return null;
                    })()
                    : null,
            };
        });
        return {
            portfolio: {
                balance: (Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.net_worth) || '0',
                apy: (Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.aggregated_apy) || '0',
                apr: (Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.aggregated_apr) || '0',
                totalTVL: parseCurrencyString((_f = protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl) === null || _f === void 0 ? void 0 : _f.total_tvl),
                airdropReserves: parseCurrencyString(protocolData === null || protocolData === void 0 ? void 0 : protocolData.airdrop_reserves),
                projectedEarnings: Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.projected_earnings,
            },
            pools,
        };
    }, [
        staticData,
        loading,
        loadingPoolId,
        protocolData,
        walletBalancesMap,
        Portfolio,
        poolTabStates,
        poolInputValues,
        coinInfoData,
        themeName,
    ]);
    // Pools load in after the first render, so seed the Withdraw tab for any pool
    // that is closed to deposits once its data arrives. Without this a pool the
    // user has not touched yet falls back to the Deposit tab.
    useEffect(() => {
        const closedPoolIds = pageData.pools
            .map((p) => p.pool.id)
            .filter((poolId) => isDepositDisabledPool(poolId));
        if (closedPoolIds.length === 0)
            return;
        setPoolTabStates((prev) => {
            const unseeded = closedPoolIds.filter((poolId) => !(poolId in prev));
            if (unseeded.length === 0)
                return prev;
            return unseeded.reduce((acc, poolId) => (Object.assign(Object.assign({}, acc), { [poolId]: 1 })), Object.assign({}, prev));
        });
    }, [pageData.pools]);
    const handleSort = (key) => {
        setSortConfig((prevConfig) => handleSortTransition(prevConfig, key));
    };
    const handleTabClick = (tab) => {
        setActiveTab(tab);
        if (tab === 'All') {
            setSearchParams({});
        }
        else {
            setSearchParams({ tab: tab.toLowerCase() });
        }
    };
    const handlePoolTabChange = (poolId, tabIndex) => {
        var _a;
        setPoolTabStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: tabIndex })));
        setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: {} })));
        setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: false })));
        // Reset the token choice to SUI on tab switch.
        setDepositTokenByPool((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: 'SUI' })));
        setWithdrawTokenByPool((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: 'SUI' })));
        setZapInQuoteDetails({
            zapInLoader: true,
            zapInQuoteLoader: false,
            poolId: ((_a = pageData.pools.find((p) => p.pool.id === poolId)) === null || _a === void 0 ? void 0 : _a.pool.strategyType) ===
                'Leveraged Yield Farming'
                ? ''
                : poolId,
            poolDetailsPage: false,
        });
        setStrategyQuoteDetails((prev) => (Object.assign(Object.assign({}, prev), { poolId: poolId, inputTokenSymbol: '', inputValue: '', poolTokens: [] })));
    };
    const handlePoolExpansionChange = (poolId, isExpanded) => {
        setPoolExpansionStates((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: isExpanded })));
        // Don't clear input values for retired Navi pools on withdraw tab
        if (!isExpanded) {
            setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolId]: {} })));
        }
    };
    const fetchProtocolNameForSearch = (protocol) => {
        return protocol.map((protocol) => protocol.toLowerCase()).join('-');
    };
    const filteredPools = useMemo(() => {
        let filtered = pageData.pools;
        // Hide specific pool
        filtered = filtered.filter((poolData) => poolData.pool.id !== '0x6ee8f60226edf48772f81e5986994745dae249c2605a5b12de6602ef1b05b0c1');
        if (activeTab === 'All') {
            filtered = filtered.filter((poolData) => {
                var _a;
                const pool = poolData.pool;
                const tag = (_a = poolData === null || poolData === void 0 ? void 0 : poolData.tags) === null || _a === void 0 ? void 0 : _a.map((tag) => tag.name.toLowerCase());
                const isSlush = tag === null || tag === void 0 ? void 0 : tag.includes('slush');
                const hasDeposit = parseFloat(pool.yourDeposit) > 0;
                // Show active slush pools
                if (isSlush && pool.isActive)
                    return true;
                // Show active non-slush pools
                if (pool.isActive && !isSlush)
                    return true;
                // Show pools where user has deposits (even if inactive)
                if (hasDeposit)
                    return true;
                return false;
            });
        }
        else if (activeTab === 'Retired') {
            filtered = filtered.filter((poolData) => {
                const pool = poolData.pool;
                return !pool.isActive;
            });
        }
        else if (activeTab === 'Stables') {
            filtered = filtered.filter((poolData) => {
                const pool = poolData.pool;
                return (pool.isActive &&
                    (pool.tab.includes('USDC') ||
                        pool.tab.includes('USDT') ||
                        pool.tab.includes('suiUSDT') ||
                        pool.tab.includes('USDSUI')));
            });
        }
        else if (activeTab === 'Slush') {
            filtered = filtered.filter((poolData) => {
                var _a, _b;
                const tag = (_a = poolData === null || poolData === void 0 ? void 0 : poolData.tags) === null || _a === void 0 ? void 0 : _a.map((tag) => tag.name.toLowerCase());
                return (_b = tag === null || tag === void 0 ? void 0 : tag.includes('slush')) !== null && _b !== void 0 ? _b : false;
            });
        }
        else {
            filtered = filtered.filter((poolData) => {
                var _a;
                const pool = poolData.pool;
                const tag = (_a = poolData === null || poolData === void 0 ? void 0 : poolData.tags) === null || _a === void 0 ? void 0 : _a.map((tag) => tag.name.toLowerCase());
                if (activeTab === 'Holdings') {
                    return parseFloat(pool.yourDeposit) > 0;
                }
                return pool.isActive && !(tag === null || tag === void 0 ? void 0 : tag.includes('slush')) && pool.tab.includes(activeTab);
            });
        }
        if (searchTerm.trim()) {
            filtered = filtered.filter((poolData) => {
                var _a, _b;
                const pool = poolData.pool;
                const name = (_a = pool.name) === null || _a === void 0 ? void 0 : _a.toLowerCase();
                const searchLower = searchTerm.toLowerCase();
                const protocol = fetchProtocolNameForSearch(pool.protocol);
                const tag = (_b = poolData === null || poolData === void 0 ? void 0 : poolData.tags) === null || _b === void 0 ? void 0 : _b.map((tag) => tag.name.toLowerCase());
                return ((name === null || name === void 0 ? void 0 : name.includes(searchLower)) ||
                    protocol.includes(searchLower) ||
                    (tag && Array.isArray(tag) && tag.includes(searchLower)));
            });
        }
        return filtered;
    }, [pageData.pools, activeTab, searchTerm]);
    const sortedPools = useMemo(() => {
        let sorted = sortPools(filteredPools, sortConfig);
        // In 'All' tab, move slush pools to the top only when no sorting is applied
        if (activeTab === 'All' && !sortConfig) {
            const slushPools = sorted.filter((poolData) => {
                var _a;
                const tag = (_a = poolData === null || poolData === void 0 ? void 0 : poolData.tags) === null || _a === void 0 ? void 0 : _a.map((tag) => tag.name.toLowerCase());
                return tag === null || tag === void 0 ? void 0 : tag.includes('slush');
            });
            const nonSlushPools = sorted.filter((poolData) => {
                var _a;
                const tag = (_a = poolData === null || poolData === void 0 ? void 0 : poolData.tags) === null || _a === void 0 ? void 0 : _a.map((tag) => tag.name.toLowerCase());
                return !(tag === null || tag === void 0 ? void 0 : tag.includes('slush'));
            });
            sorted = [...slushPools, ...nonSlushPools];
        }
        // Move the SUI Multiplier pool to the top only when no sorting is applied
        if (!sortConfig) {
            const isSuiMultiplier = (poolData) => { var _a; return ((_a = poolData.pool.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'sui multiplier'; };
            sorted = [...sorted.filter(isSuiMultiplier), ...sorted.filter((p) => !isSuiMultiplier(p))];
        }
        return sorted;
    }, [filteredPools, sortConfig, activeTab]);
    const fetchZapInQuote = useCallback((depositAmount_1, isInputA_1, pool_1, tokens_1, ...args_1) => __awaiter(void 0, [depositAmount_1, isInputA_1, pool_1, tokens_1, ...args_1], void 0, function* (depositAmount, isInputA, pool, tokens, freshQuote = true) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        try {
            setZapInQuoteDetails((prev) => {
                var _a;
                return (Object.assign(Object.assign({}, prev), { zapInLoader: freshQuote ? true : false, zapInQuoteLoader: freshQuote ? true : false, poolId: ((_a = pageData.pools.find((p) => p.pool.id === pool)) === null || _a === void 0 ? void 0 : _a.pool.strategyType) ===
                        'Leveraged Yield Farming'
                        ? ''
                        : pool, poolDetailsPage: false }));
            });
            const poolDetails = staticData === null || staticData === void 0 ? void 0 : staticData.find((pooldata) => pooldata.pool_id === pool);
            const tokenDetails = isInputA
                ? (_a = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _a === void 0 ? void 0 : _a[0]
                : (_b = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _b === void 0 ? void 0 : _b[1];
            const otherTokenDetails = isInputA
                ? (_c = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _c === void 0 ? void 0 : _c[1]
                : (_d = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _d === void 0 ? void 0 : _d[0];
            // if (poolDetails?.is_reversed) {
            //   [tokenDetails, otherTokenDetails] = [otherTokenDetails, tokenDetails];
            // }
            if ((tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_type) && (otherTokenDetails === null || otherTokenDetails === void 0 ? void 0 : otherTokenDetails.asset_type)) {
                const tokenBalance = (parseFloat((walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_type)) || '0') /
                    Math.pow(10, (tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_decimals)))
                    .toFixed(tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_decimals)
                    .toString();
                if (tokenBalance && parseFloat(tokenBalance) >= parseFloat(depositAmount)) {
                    //fetch get quote from sdk
                    const depositAmountNormalized = (parseFloat(depositAmount) *
                        Math.pow(10, (tokenDetails === null || tokenDetails === void 0 ? void 0 : tokenDetails.asset_decimals)))
                        .toString()
                        .split('.')[0];
                    const alphafiSdk = createAlphaFiSDK(network);
                    const quote = yield alphafiSdk.getZapDepositQuote({
                        poolId: (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id) || '',
                        inputCoinAmount: BigInt(depositAmountNormalized),
                        isInputA: (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.is_reversed) ? !isInputA : isInputA,
                        address: address || '',
                        slippage: 0.01,
                    });
                    if (quote && poolDetails) {
                        const token1Amount = (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.is_reversed)
                            ? (parseFloat(quote[1]) / Math.pow(10, ((_f = (_e = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _e === void 0 ? void 0 : _e[1]) === null || _f === void 0 ? void 0 : _f.asset_decimals)))
                                .toFixed(4)
                                .toString()
                            : (parseFloat(quote[0]) / Math.pow(10, ((_h = (_g = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _g === void 0 ? void 0 : _g[0]) === null || _h === void 0 ? void 0 : _h.asset_decimals)))
                                .toFixed(4)
                                .toString();
                        const token2Amount = (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.is_reversed)
                            ? (parseFloat(quote[0]) / Math.pow(10, ((_k = (_j = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _j === void 0 ? void 0 : _j[0]) === null || _k === void 0 ? void 0 : _k.asset_decimals)))
                                .toFixed(4)
                                .toString()
                            : (parseFloat(quote[1]) / Math.pow(10, ((_m = (_l = poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.asset_types) === null || _l === void 0 ? void 0 : _l[1]) === null || _m === void 0 ? void 0 : _m.asset_decimals)))
                                .toFixed(4)
                                .toString();
                        const token1Price = (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.is_reversed) ? tokens[1].price : tokens[0].price;
                        const token2Price = (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.is_reversed) ? tokens[0].price : tokens[1].price;
                        const token1AmountInUsd = token1Price && parseFloat(token1Amount) * parseFloat(token1Price) > 0.01
                            ? (parseFloat(token1Amount) * parseFloat(token1Price)).toFixed(2).toString()
                            : token1Price && parseFloat(token1Amount) * parseFloat(token1Price) < 0.01
                                ? (parseFloat(token1Amount) * parseFloat(token1Price)).toFixed(4).toString()
                                : '0';
                        const token2AmountInUsd = token2Price && parseFloat(token2Amount) * parseFloat(token2Price) > 0.01
                            ? (parseFloat(token2Amount) * parseFloat(token2Price)).toFixed(2).toString()
                            : token2Price && parseFloat(token2Amount) * parseFloat(token2Price) < 0.01
                                ? (parseFloat(token2Amount) * parseFloat(token2Price)).toFixed(4).toString()
                                : '0';
                        const inputUSD = token1Price && isInputA
                            ? parseFloat(depositAmount) * parseFloat(token1Price)
                            : token2Price
                                ? parseFloat(depositAmount) * parseFloat(token2Price)
                                : 0;
                        const tokenShareInZap = token1AmountInUsd || token2AmountInUsd
                            ? [
                                ((parseFloat(token1AmountInUsd) * 100) /
                                    (parseFloat(token1AmountInUsd) + parseFloat(token2AmountInUsd)))
                                    .toFixed(2)
                                    .toString(),
                                ((parseFloat(token2AmountInUsd) * 100) /
                                    (parseFloat(token1AmountInUsd) + parseFloat(token2AmountInUsd)))
                                    .toFixed(2)
                                    .toString(),
                            ]
                            : ['0', '0'];
                        const zapSlippage = (((inputUSD - (parseFloat(token1AmountInUsd) + parseFloat(token2AmountInUsd))) *
                            100) /
                            inputUSD)
                            .toFixed(2)
                            .toString();
                        const swapAmountArray = isInputA
                            ? [
                                (inputUSD - parseFloat(token1AmountInUsd) / parseFloat(token1Price))
                                    .toFixed(4)
                                    .toString(),
                                token2Amount,
                            ]
                            : [
                                token1Amount,
                                (inputUSD - parseFloat(token2AmountInUsd) / parseFloat(token2Price))
                                    .toFixed(4)
                                    .toString(),
                            ];
                        setZapInQuoteDetails({
                            token1Amount,
                            token2Amount,
                            token1AmountInUsd,
                            token2AmountInUsd,
                            token1Icon: tokens[0].icon,
                            token2Icon: tokens[1].icon,
                            tokenShareInZap: tokenShareInZap,
                            zapSlippage,
                            token1Price,
                            token2Price,
                            token1Symbol: tokens[0].symbol,
                            token2Symbol: tokens[1].symbol,
                            isInputA,
                            swapAmountArray,
                            depositAmount: depositAmountNormalized.toString(),
                            depositAmountWithoutExponent: depositAmount.toString(),
                            reverseIcon: themeName === 'dark'
                                ? getIcon('reverseTokensIconDarkTheme')
                                : getIcon('reverseTokensIcon'),
                            slippageIcon: themeName === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('zapSlippageIcon'),
                            zapInIcon: themeName === 'dark' && isZapInActive
                                ? getIcon('zapInIcon')
                                : themeName === 'dark' && !isZapInActive
                                    ? getIcon('zapInIconDarkTheme')
                                    : themeName !== 'dark' && isZapInActive
                                        ? getIcon('zapInIconDarkTheme')
                                        : themeName !== 'dark' && !isZapInActive
                                            ? getIcon('zapInIcon')
                                            : '',
                            depositsIcon: themeName === 'dark'
                                ? getIcon('depositsIconForDarkTheme')
                                : getIcon('depositsIcon'),
                            zapInLoader: false,
                            zapInQuoteLoader: false,
                            poolId: ((_o = pageData.pools.find((p) => p.pool.id === (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id))) === null || _o === void 0 ? void 0 : _o.pool.strategyType) === 'Leveraged Yield Farming'
                                ? ''
                                : poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id,
                            activeToken: isInputA ? tokens[0].symbol : tokens[1].symbol,
                            customZapSlippagePopupStyle: 'absolute left-[27%] mt-[25px] z-50',
                            poolDetailsPage: false,
                        });
                    }
                }
                else {
                    setZapInQuoteDetails({
                        zapInLoader: true,
                        zapInQuoteLoader: false,
                        poolId: ((_p = pageData.pools.find((p) => p.pool.id === (poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id))) === null || _p === void 0 ? void 0 : _p.pool.strategyType) === 'Leveraged Yield Farming'
                            ? ''
                            : poolDetails === null || poolDetails === void 0 ? void 0 : poolDetails.pool_id,
                        activeToken: isInputA ? tokens[0].symbol : tokens[1].symbol,
                        poolDetailsPage: false,
                        slippageIcon: themeName === 'dark' ? getIcon('whiteSlippageIcon') : getIcon('zapSlippageIcon'),
                    });
                }
            }
        }
        catch (error) {
            console.log('error in fetching zapin quote', error);
            setZapInQuoteDetails({
                zapInLoader: false,
                zapInQuoteLoader: false,
                poolId: ((_q = pageData.pools.find((p) => p.pool.id === pool)) === null || _q === void 0 ? void 0 : _q.pool.id) || '',
                activeToken: isInputA ? tokens[0].symbol : tokens[1].symbol,
                poolDetailsPage: false,
            });
        }
    }), [pageData.pools, staticData, walletBalancesMap, network, address, themeName, isZapInActive]);
    // Refetch zap in quote periodically
    useEffect(() => {
        const poolData = pageData.pools.find((p) => p.pool.id === zapInQuoteDetails.poolId);
        if (zapInQuoteDetails.poolId &&
            zapInQuoteDetails.activeToken &&
            zapInQuoteDetails.depositAmountWithoutExponent &&
            (poolData === null || poolData === void 0 ? void 0 : poolData.tokens) &&
            (poolData === null || poolData === void 0 ? void 0 : poolData.tokens.length) === 2 &&
            zapInQuoteDetails.token1Amount !== '' &&
            zapInQuoteDetails.token2Amount !== '') {
            const interval = setInterval(() => {
                fetchZapInQuote(zapInQuoteDetails.depositAmountWithoutExponent || '', zapInQuoteDetails.activeToken === (poolData === null || poolData === void 0 ? void 0 : poolData.tokens[0].symbol), zapInQuoteDetails.poolId || '', poolData === null || poolData === void 0 ? void 0 : poolData.tokens, false);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [
        zapInQuoteDetails.poolId,
        zapInQuoteDetails.activeToken,
        zapInQuoteDetails.depositAmountWithoutExponent,
        pageData.pools,
        fetchZapInQuote,
        zapInQuoteDetails.token1Amount,
        zapInQuoteDetails.token2Amount,
    ]);
    // const projectedEarningsData: Record<string, string | undefined> = {
    //   '1D': Portfolio?.projected_earnings?.one_days_earnings,
    //   '7D': Portfolio?.projected_earnings?.seven_days_earnings,
    //   '30D': Portfolio?.projected_earnings?.thirty_days_earnings,
    // };
    // const [activePeriod, setActivePeriod] = useState('1D');
    // const handleProjectedEarningsChange = (period: string) => {
    //   setActivePeriod(period);
    // };
    const displayLoaderConditions = (poolId) => {
        // A transaction is in flight — show the spinner on the row that started it. Keyed to
        // loadingPoolId rather than the quote state's poolId: the quote state follows the last
        // row the user interacted with, which is not necessarily the row being transacted on.
        if (loading && loadingPoolId === poolId) {
            return true;
        }
        // A quote is being fetched for this row. These are genuinely quote-scoped, so they stay
        // keyed to the quote state.
        if ((strategyQuoteDetails === null || strategyQuoteDetails === void 0 ? void 0 : strategyQuoteDetails.poolId) === poolId && strategyQuoteLoader) {
            return true;
        }
        if (isZapInActive &&
            (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.poolId) === poolId &&
            (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.zapInQuoteLoader) === true) {
            return true;
        }
        return false;
    };
    useEffect(() => {
        if (isAutoSlippage) {
            const autoSlippage = getAutoSlippage((zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA)
                ? zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol
                : zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol, (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA)
                ? zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol
                : zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol);
            setSlippage(autoSlippage);
        }
    }, [
        zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA,
        zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol,
        zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol,
        isAutoSlippage,
    ]);
    return (_jsxs("div", { className: "theme-bg-alphafi-main-body min-h-screen pb-[150px] xl:pb-[267px] -mt-[1px]", children: [_jsx(DashboardStats, { isWalletConnected: address, userStats: [
                    {
                        value: `$${convertToKAndM(pageData.portfolio.balance)}`,
                        label: 'Net Worth',
                        showHoverInfo: true,
                        unparsedValue: `$${formatHoverNumber(pageData.portfolio.balance)}`,
                    },
                    {
                        value: `$${convertToKAndM(((_a = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.projected_earnings) === null || _a === void 0 ? void 0 : _a.one_days_earnings) || '0')}`,
                        label: 'Daily Earnings',
                        showHoverInfo: true,
                        unparsedValue: `$${formatHoverNumber(((_b = Portfolio === null || Portfolio === void 0 ? void 0 : Portfolio.projected_earnings) === null || _b === void 0 ? void 0 : _b.one_days_earnings) || '0')}`,
                    },
                    {
                        value: [
                            `${convertToKAndM(pageData.portfolio.apy)}%`,
                            `${convertToKAndM(pageData.portfolio.apr)}%`,
                        ],
                        label: ['APY', 'APR'],
                        labelSuffixIcon: getIcon('viceVersaIconWhite'),
                        labelSuffixIconStyle: 'w-[12px] h-[12px] md:w-[16px] md:h-[16px] cursor-pointer',
                    },
                ], protocolStats: [
                    {
                        value: `$${convertToKAndM(((_c = protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl) === null || _c === void 0 ? void 0 : _c.alphafi_tvl) || '0')}`,
                        label: 'Strategies TVL',
                        showHoverInfo: true,
                        unparsedValue: `$${formatHoverNumber(((_d = protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl) === null || _d === void 0 ? void 0 : _d.alphafi_tvl) || '0')}`,
                    },
                    {
                        value: `$${convertToKAndM(pageData.portfolio.airdropReserves)}`,
                        label: 'ALPHA Reserves',
                    },
                ], defaultStats: {
                    title: 'Deposit and Earn. Real Yields. Fully Automated',
                    stats: [
                        {
                            value: `$${convertToKAndM(((_e = protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl) === null || _e === void 0 ? void 0 : _e.alphafi_tvl) || '0')}`,
                            label: 'Strategies TVL',
                            showHoverInfo: true,
                            unparsedValue: `$${formatHoverNumber(((_f = protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl) === null || _f === void 0 ? void 0 : _f.alphafi_tvl) || '0')}`,
                        },
                        {
                            value: `$${convertToKAndM(pageData.portfolio.airdropReserves)}`,
                            label: 'ALPHA Reserves',
                        },
                    ],
                    walletConnectFunctionality: walletConnectFunctionality,
                    bgImage: themeName === 'dark' && isMobile
                        ? getImage('dashboardDarkMobileBg')
                        : themeName !== 'dark' && isMobile
                            ? getImage('dashboardLightMobileBg')
                            : themeName === 'dark' && !isMobile
                                ? getImage('dashboardDarkBg')
                                : getImage('dashboardLightBg'),
                } }), _jsxs("div", { className: "flex lg:flex-row lg:max-w-[985px] xl:max-w-[1390px] w-full flex-col-reverse justify-center items-center md:justify-between mx-auto px-5 md:px-[unset]", children: [_jsx(FilterTab, { tabs: AVAILABLE_TABS.filter((tab) => tab !== 'Retired'), activeTab: activeTab, handleTabClick: handleTabClick }), _jsx("div", { className: "flex justify-end w-full md:px-[unset] mb-[26px] lg:mb-[unset] md:mr-[34px] lg:mr-[unset] mr-[unset]", children: _jsx(SearchInput, { search: searchTerm, handleSearchChange: (e) => setSearchTerm(e.target.value), clearSearch: () => setSearchTerm(''), clearSearchIcon: getIcon('clearSearchIcon'), searchIcon: themeName === 'dark'
                                ? getIcon('darkThemeSearchIcon')
                                : getIcon('defaultThemeSearchIcon') }) })] }), _jsx("div", { className: "w-full flex justify-center mx-auto mt-4 md:mt-11", children: _jsx(RowHeader, { sortConfig: sortConfig || undefined, onSort: (key) => handleSort(key), columns: [
                        { key: 'assets', label: 'Assets', align: 'left', showOnMobile: true },
                        { key: 'protocols', label: 'Protocols', align: 'center', showOnMobile: true },
                        { key: 'apy', label: 'APY', align: 'center', showOnMobile: true },
                        { key: 'tvl', label: 'TVL', align: 'center', showOnMobile: false },
                        { key: 'your_deposit', label: 'Your Deposit', align: 'center', showOnMobile: true },
                    ], sortUpIcon: themeName === 'dark' ? getIcon('darkThemeUpArrow') : getIcon('upArrowBlack'), sortDownIcon: themeName === 'dark' ? getIcon('darkThemeDownArrow') : getIcon('downArrowBlack'), sortDefaultIcon: themeName === 'dark' ? getIcon('darkThemeDefaultArrow') : getIcon('defaultArrow') }) }), _jsx("div", { className: "flex flex-col w-full justify-center items-center space-y-3.5 xl:space-y-5 md:py-3 lg:py-4 px-4 xl:px-0", children: sortedPools.map((poolData) => (_jsx(Row, { pool: poolData.pool, tokens: poolData.tokens, inputTokens: buildRowTokens(poolData), navTabs: poolData.pool.navTabs, tags: poolData.tags, tooltip: poolData.tooltip, aboutStrategy: poolData.aboutStrategy, isLoading: displayLoaderConditions(poolData.pool.id), activeTabIndex: poolTabStates[poolData.pool.id] || 0, onTabChange: (tabIndex) => handlePoolTabChange(poolData.pool.id, tabIndex), isExpanded: poolExpansionStates[poolData.pool.id] || false, onExpansionChange: (isExpanded) => poolData.pool.name !== 'ALPHA'
                        ? handlePoolExpansionChange(poolData.pool.id, isExpanded)
                        : navigate(`/ALPHA`), onDeposit: (amounts) => handleDeposit(poolData.pool.id, amounts), onWithdraw: (amounts) => handleWithdraw(poolData.pool.id, amounts), onZapInAndDeposit: () => handleZapInAndDeposit(poolData.pool.id), onMaxClick: (tokenSymbol) => handleMaxClick(poolData.pool.id, tokenSymbol), onTokenValueChange: (tokenSymbol, value) => {
                        var _a, _b;
                        setPoolMaxStates((prev) => (Object.assign(Object.assign({}, prev), { [poolData.pool.id]: false })));
                        const currentPool = pageData.pools.find((p) => p.pool.id === poolData.pool.id);
                        if (!currentPool || currentPool.tokens.length !== 2) {
                            return { [tokenSymbol]: value };
                        }
                        const debounceKey = poolData.pool.id;
                        if (tokenValueDebounceRefs.current[debounceKey]) {
                            clearTimeout(tokenValueDebounceRefs.current[debounceKey]);
                        }
                        if (value.trim() === '') {
                            const clearedValues = Object.fromEntries(currentPool.tokens.map((t) => [t.symbol, '']));
                            setPoolInputValues((prev) => (Object.assign(Object.assign({}, prev), { [poolData.pool.id]: clearedValues })));
                            setZapInQuoteDetails((prev) => {
                                var _a;
                                return (Object.assign(Object.assign({}, prev), { zapInLoader: true, zapInQuoteLoader: false, poolId: ((_a = pageData.pools.find((p) => p.pool.id === poolData.pool.id)) === null || _a === void 0 ? void 0 : _a.pool.strategyType) === 'Leveraged Yield Farming'
                                        ? ''
                                        : poolData.pool.id, poolDetailsPage: false, zapSlippage: '' }));
                            });
                            return clearedValues;
                        }
                        const immediateResult = {
                            [tokenSymbol]: value,
                            [((_a = currentPool.tokens.find((t) => t.symbol !== tokenSymbol)) === null || _a === void 0 ? void 0 : _a.symbol) || '']: '',
                        };
                        setPoolInputValues((prev) => {
                            var _a;
                            return (Object.assign(Object.assign({}, prev), { [poolData.pool.id]: Object.assign(Object.assign({}, prev[poolData.pool.id]), { [tokenSymbol]: value, [((_a = currentPool.tokens.find((t) => t.symbol !== tokenSymbol)) === null || _a === void 0 ? void 0 : _a.symbol) || '']: '' }) }));
                        });
                        if (currentPool.tokens.length === 2 &&
                            poolTabStates &&
                            (poolTabStates[poolData.pool.id] === 0 ||
                                poolTabStates[poolData.pool.id] === undefined) &&
                            isZapInActive &&
                            parseFloat(value) === 0) {
                            setZapInQuoteDetails({
                                zapInLoader: true,
                                zapInQuoteLoader: false,
                                poolId: ((_b = pageData.pools.find((p) => p.pool.id === poolData.pool.id)) === null || _b === void 0 ? void 0 : _b.pool.strategyType) === 'Leveraged Yield Farming'
                                    ? ''
                                    : poolData.pool.id,
                                activeToken: tokenSymbol,
                                poolDetailsPage: false,
                            });
                            return immediateResult;
                        }
                        tokenValueDebounceRefs.current[debounceKey] = setTimeout(() => {
                            if (currentPool.tokens.length === 2 &&
                                poolTabStates &&
                                (poolTabStates[poolData.pool.id] === 0 ||
                                    poolTabStates[poolData.pool.id] === undefined) &&
                                isZapInActive &&
                                parseFloat(value) > 0) {
                                fetchZapInQuote(value, tokenSymbol === currentPool.tokens[0].symbol, poolData.pool.id, currentPool.tokens);
                            }
                            else if (parseFloat(value) > 0) {
                                setStrategyQuoteDetails((prev) => (Object.assign(Object.assign({}, (prev || {})), { poolId: poolData.pool.id, inputTokenSymbol: tokenSymbol, inputValue: value, poolTokens: currentPool.tokens })));
                                calculateTokenRatios(poolData.pool.id, tokenSymbol, value, currentPool.tokens);
                            }
                        }, 800);
                        return immediateResult;
                    }, onTokenInputChange: (tokenSymbol, value) => handleTokenInputChange(poolData.pool.id, tokenSymbol, value), poolRedirectHandler: () => {
                        navigate(`/pool-details/${poolData.pool.name}?id=${poolData.pool.id}`);
                    }, balanceText: poolTabStates[poolData.pool.id] === 0 || !(poolData.pool.id in poolTabStates)
                        ? 'In Wallet:'
                        : 'In Vault:', isButtonDisabled: (() => {
                        const buttonState = getButtonState({
                            inputValues: poolInputValues[poolData.pool.id],
                            tabIndex: poolTabStates[poolData.pool.id],
                            tokens: buildRowTokens(poolData),
                            maxClicked: poolMaxStates[poolData.pool.id] || false,
                            rangeStatus: poolData.pool.rangeStatus,
                            depositDisabled: isDepositDisabledPool(poolData.pool.id),
                        });
                        return buttonState.disabled;
                    })(), buttonText: (() => {
                        // rangeStatus is deliberately omitted: out-of-range only gates the
                        // button, it must not mask the "exceeds balance" text.
                        const buttonState = getButtonState({
                            inputValues: poolInputValues[poolData.pool.id],
                            tabIndex: poolTabStates[poolData.pool.id],
                            tokens: buildRowTokens(poolData),
                            maxClicked: poolMaxStates[poolData.pool.id] || false,
                            depositDisabled: isDepositDisabledPool(poolData.pool.id),
                        });
                        return buttonState.text;
                    })(), walletConnectFunctionality: address ? undefined : walletConnectFunctionality, zapinIcon: themeName === 'dark' && isZapInActive
                        ? getIcon('zapInIcon')
                        : themeName === 'dark' && !isZapInActive
                            ? getIcon('zapInIconDarkTheme')
                            : themeName !== 'dark' && isZapInActive
                                ? getIcon('zapInIconDarkTheme')
                                : themeName !== 'dark' && !isZapInActive
                                    ? getIcon('zapInIcon')
                                    : '', switchTokenIcon: themeName === 'dark'
                        ? getIcon('switchTokenDarkThemeIcon')
                        : getIcon('switchTokenIcon'), zapInQuoteDetails: zapInQuoteDetails, isZapInActive: poolData.pool.isActive && isZapInActive, setIsZapInActive: setIsZapInActive, slippage: slippage, setSlippage: setSlippage, pendingWithdrawals: poolData.pendingWithdrawals, claimSlushPendingWithdrawals: poolData.pool.id === SLUSH_WAL_LOOPING_POOL
                        ? (withdrawRequestId, withdrawalAmount) => handleClaimPendingWithdrawalsWrapper(poolData.pool.id, withdrawRequestId, withdrawalAmount)
                        : undefined, cancelSlushPendingWithdrawals: poolData.pool.id === SLUSH_WAL_LOOPING_POOL
                        ? (withdrawRequestId, withdrawalAmount) => handleCancelPendingWithdrawalsWrapper(poolData.pool.id, withdrawRequestId, withdrawalAmount)
                        : undefined, loadingButtonId: loadingWithdrawalId, downArrowIcon: themeName === 'dark' ? getIcon('whiteDownArrow') : getIcon('blackDownArrow'), pendingWithdrawalsLabel: poolData.pool.id === SLUSH_WAL_LOOPING_POOL ? 'Pending Withdrawal' : undefined, lockIcon: getIcon('blackLockIcon'), isAutoSlippage: isAutoSlippage, onAutoSlippageChange: (isAuto) => {
                        setIsAutoSlippage(isAuto);
                        if (isAuto) {
                            setSlippage(getAutoSlippage((zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA)
                                ? zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol
                                : zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol, (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA)
                                ? zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol
                                : zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol));
                        }
                    }, autoSlippageValue: getAutoSlippage((zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA)
                        ? zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol
                        : zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol, (zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.isInputA)
                        ? zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token2Symbol
                        : zapInQuoteDetails === null || zapInQuoteDetails === void 0 ? void 0 : zapInQuoteDetails.token1Symbol), reverseIcon: themeName === 'dark'
                        ? getIcon('reverseTokensIconDarkTheme')
                        : getIcon('reverseTokensIcon'), depositTokenOptions: poolData.pool.id === SUI_MULTIPLIER_POOL_ID ? depositTokenOptions : undefined, selectedDepositToken: getSelectedDepositToken(poolData.pool.id), onDepositTokenSelect: (symbol) => handleDepositTokenSelect(poolData.pool.id, symbol), withdrawTokenOptions: poolData.pool.id === SUI_MULTIPLIER_POOL_ID ? depositTokenOptions : undefined, selectedWithdrawToken: getSelectedWithdrawToken(poolData.pool.id), onWithdrawTokenSelect: (symbol) => handleWithdrawTokenSelect(poolData.pool.id, symbol) }, poolData.pool.id))) }), _jsx(SwapButton, { isSwapModalOpen: isSwapModalOpen, onClick: () => setIsSwapModalOpen(true), themeName: themeName }), _jsx(SwapModal, { isOpen: isSwapModalOpen, onClose: () => setIsSwapModalOpen(false), onNotification: handleSwapNotification, loading: loading, setLoading: setLoading, walletConnectFunctionality: walletConnectFunctionality, isWalletConnected: address ? true : false, theme: themeName }), _jsx(NotificationComp, { isOpen: isNotificationOpen, setIsOpen: setIsNotificationOpen, info: notificationInfo })] }));
};
export default PoolsPage;
